import { useEffect, useMemo, useState, type JSX, type ReactElement } from "react";
import { BUILDING_INFO, RESEARCH_NODE_BY_ID, type BuildingFamily } from "@stahlfront/core";
import type { ConstructionJobDto, ProductionJobDto, ResearchJobDto } from "@stahlfront/shared";
import { useStore } from "../store.js";

/**
 * §6.4 Operations Dashboard — left-rail active-tasks column.
 *
 * Phase 2.E ships the rail itself + two live categories (Production §6.4.2
 * and Construction §6.4.3). The other six §6.4.* categories are surfaced as
 * disabled rail icons so the muscle memory locks in early; they will light
 * up as their underlying systems land in later phases. Per spec §6.4 the
 * rail stays accessible from any zoom layer; we mount it inside GameView so
 * it persists across BASE / WORLD / TACTICAL.
 *
 * Counter-badge contract:
 *   - Production: red badge if any job is state=complete (claim ready),
 *     green badge if jobs are still active (in progress), hidden otherwise.
 *   - Construction: green badge per active upgrade row.
 *   - Disabled categories: never show a badge.
 */

const STROKE_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

type RailKey =
  | "search"
  | "tasks"
  | "resources"
  | "production"
  | "construction"
  | "research"
  | "marches"
  | "buffs";

interface RailDef {
  readonly key: RailKey;
  readonly label: string;
  readonly icon: ReactElement;
  readonly enabled: boolean;
  /** Phase-2 placeholder copy shown via tooltip on disabled icons. */
  readonly placeholderHint: string;
}

function SearchIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <circle cx="11" cy="11" r="6.2" />
        <path d="M15.6 15.6 L20 20" />
      </g>
    </svg>
  );
}

function TasksIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <path d="M6.5 8 L9 8 M6.5 12 L13 12 M6.5 16 L11 16" />
        <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      </g>
    </svg>
  );
}

function ResourcesIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <path d="M3.5 17 L11 17 L11 11 L17 11 L17 17 L20.5 17" />
        <circle cx="6.5" cy="19.5" r="1.6" />
        <circle cx="17.5" cy="19.5" r="1.6" />
        <path d="M11 11 L11 7" />
      </g>
    </svg>
  );
}

function ProductionIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <circle cx="12" cy="12" r="3.4" />
        <path d="M12 4.2 L12 6.8 M12 17.2 L12 19.8 M4.2 12 L6.8 12 M17.2 12 L19.8 12 M6.4 6.4 L8.2 8.2 M15.8 15.8 L17.6 17.6 M6.4 17.6 L8.2 15.8 M15.8 8.2 L17.6 6.4" />
      </g>
    </svg>
  );
}

function ConstructionIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <path d="M4.5 9.5 L9 5 L19.5 15.5 L15 20 Z" />
        <path d="M11.5 7.5 L17 13" />
        <path d="M3.5 20.5 L9.5 14.5" />
      </g>
    </svg>
  );
}

function ResearchIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <path d="M9.5 3.5 L9.5 8.5 L4.5 17.5 C 3.8 18.8 4.7 20.5 6.2 20.5 L17.8 20.5 C 19.3 20.5 20.2 18.8 19.5 17.5 L14.5 8.5 L14.5 3.5" />
        <path d="M8 3.5 L16 3.5" />
      </g>
    </svg>
  );
}

function MarchesIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <path d="M5 19.5 L9 13 L13 16 L19 6.5" />
        <path d="M14.5 6.5 L19 6.5 L19 11" />
      </g>
    </svg>
  );
}

function BuffsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <g {...STROKE_PROPS}>
        <path d="M12 3 L19.5 6 L19.5 13 C 19.5 17 16.5 19.5 12 21 C 7.5 19.5 4.5 17 4.5 13 L4.5 6 Z" />
        <path d="M12 8 L12 15" />
        <path d="M8.5 11.5 L15.5 11.5" />
      </g>
    </svg>
  );
}

const RAIL: ReadonlyArray<RailDef> = [
  { key: "search", label: "Search", icon: <SearchIcon />, enabled: false, placeholderHint: "Search lands with the world map (Phase 4)" },
  { key: "tasks", label: "Tasks", icon: <TasksIcon />, enabled: false, placeholderHint: "Task feed lands with missions (Phase 3)" },
  { key: "resources", label: "Resources", icon: <ResourcesIcon />, enabled: false, placeholderHint: "Collector orders land with the world map (Phase 4)" },
  { key: "production", label: "Production", icon: <ProductionIcon />, enabled: true, placeholderHint: "" },
  { key: "construction", label: "Construction", icon: <ConstructionIcon />, enabled: true, placeholderHint: "" },
  { key: "research", label: "Research", icon: <ResearchIcon />, enabled: true, placeholderHint: "" },
  { key: "marches", label: "Marches", icon: <MarchesIcon />, enabled: false, placeholderHint: "Force dispatch lands with the world map (Phase 4)" },
  { key: "buffs", label: "Buffs", icon: <BuffsIcon />, enabled: false, placeholderHint: "Combat-buffs panel lands in Phase 3" },
];

interface BadgeState {
  readonly label: string;
  readonly tone: "red" | "green";
}

function pickBadge(
  key: RailKey,
  prod: readonly ProductionJobDto[],
  cons: readonly ConstructionJobDto[],
  res: readonly ResearchJobDto[],
): BadgeState | null {
  if (key === "production") {
    const ready = prod.filter((j) => j.state === "complete").length;
    if (ready > 0) return { label: ready > 99 ? "99+" : String(ready), tone: "red" };
    const active = prod.filter((j) => j.state === "active").length;
    if (active > 0) return { label: active > 99 ? "99+" : String(active), tone: "green" };
    return null;
  }
  if (key === "construction") {
    const active = cons.filter((j) => j.state === "active").length;
    if (active > 0) return { label: active > 99 ? "99+" : String(active), tone: "green" };
    return null;
  }
  if (key === "research") {
    const active = res.filter((j) => j.state === "active").length;
    if (active > 0) return { label: active > 99 ? "99+" : String(active), tone: "green" };
    return null;
  }
  return null;
}

export function OperationsDashboard(): JSX.Element | null {
  const me = useStore((s) => s.me);
  const opsCategory = useStore((s) => s.opsCategory);
  const setOpsCategory = useStore((s) => s.setOpsCategory);
  const productionJobs = useStore((s) => s.productionJobs);
  const constructionJobs = useStore((s) => s.constructionJobs);
  const researchJobs = useStore((s) => s.researchJobs);

  // Esc closes the rail panel without dismissing the rail itself.
  useEffect(() => {
    if (!opsCategory) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpsCategory(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opsCategory, setOpsCategory]);

  if (!me) return null;

  return (
    <div className="ops-root" role="region" aria-label="Operations dashboard">
      <nav className="ops-rail" aria-label="Operations rail">
        {RAIL.map((r) => {
          const badge = r.enabled
            ? pickBadge(r.key, productionJobs, constructionJobs, researchJobs)
            : null;
          const active = opsCategory === r.key;
          const onClick = (): void => {
            if (!r.enabled) return;
            setOpsCategory(active ? null : r.key);
          };
          return (
            <button
              key={r.key}
              type="button"
              className={`ops-rail-btn${active ? " ops-rail-btn-active" : ""}${r.enabled ? "" : " ops-rail-btn-disabled"}`}
              onClick={onClick}
              aria-label={r.enabled ? r.label : `${r.label} — ${r.placeholderHint}`}
              aria-pressed={active}
              aria-disabled={!r.enabled}
              title={r.enabled ? r.label : r.placeholderHint}
            >
              <span className="ops-rail-icon" aria-hidden="true">{r.icon}</span>
              {badge ? (
                <span
                  className={`ops-rail-badge ops-rail-badge-${badge.tone}`}
                  aria-label={`${badge.label} ${r.label.toLowerCase()} ${badge.tone === "red" ? "ready to claim" : "in progress"}`}
                >
                  {badge.label}
                </span>
              ) : null}
              <span className="ops-rail-tooltip" aria-hidden="true">{r.label}</span>
            </button>
          );
        })}
      </nav>

      {opsCategory ? <OpsPanel category={opsCategory} /> : null}
    </div>
  );
}

interface OpsPanelProps {
  readonly category: RailKey;
}

function OpsPanel({ category }: OpsPanelProps): JSX.Element {
  const setOpsCategory = useStore((s) => s.setOpsCategory);
  const productionJobs = useStore((s) => s.productionJobs);
  const constructionJobs = useStore((s) => s.constructionJobs);
  const researchJobs = useStore((s) => s.researchJobs);
  const researchSlots = useStore((s) => s.researchSlots);
  const buildings = useStore((s) => s.buildings);
  const setSelectedBuildingId = useStore((s) => s.setSelectedBuildingId);
  const setProductionPanelOpen = useStore((s) => s.setProductionPanelOpen);
  const setProductionPanelBuildingId = useStore((s) => s.setProductionPanelBuildingId);
  const setResearchPanelOpen = useStore((s) => s.setResearchPanelOpen);
  const setSpeedupConfirm = useStore((s) => s.setSpeedupConfirm);

  // Force a 1Hz re-render so ETAs stay live without per-row timers.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click — backdrop covers the rest of the screen but not the rail.
  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(".ops-panel") || target.closest(".ops-rail")) return;
      setOpsCategory(null);
    };
    window.addEventListener("pointerdown", onClick);
    return () => window.removeEventListener("pointerdown", onClick);
  }, [setOpsCategory]);

  const onShowProduction = (buildingId: string): void => {
    setProductionPanelBuildingId(buildingId);
    setProductionPanelOpen(true);
    setOpsCategory(null);
  };
  const onShowConstruction = (buildingId: string): void => {
    setSelectedBuildingId(buildingId);
    setOpsCategory(null);
  };
  const onSpeedupConstruction = (jobId: string): void => {
    setSpeedupConfirm({ kind: "construction", jobId });
  };
  const onSpeedupResearch = (jobId: string): void => {
    setSpeedupConfirm({ kind: "research", jobId });
  };
  const onOpenResearch = (): void => {
    setResearchPanelOpen(true);
    setOpsCategory(null);
  };

  const meta = useMemo(() => {
    if (category === "production") {
      const grouped = groupProduction(productionJobs);
      return { title: "Active production", count: grouped.length, isEmpty: grouped.length === 0, grouped };
    }
    if (category === "construction") {
      const active = constructionJobs.filter((j) => j.state === "active");
      return { title: "Construction", count: active.length, isEmpty: active.length === 0, active };
    }
    if (category === "research") {
      const active = researchJobs.filter((j) => j.state === "active");
      return { title: "Research", count: active.length, isEmpty: active.length === 0, research: active, slots: researchSlots };
    }
    return { title: "", count: 0, isEmpty: true };
  }, [category, productionJobs, constructionJobs, researchJobs, researchSlots]);

  return (
    <aside className="ops-panel" role="dialog" aria-label={`${meta.title} panel`}>
      <header className="ops-panel-head">
        <h3 className="ops-panel-title">{meta.title}</h3>
        <span className="ops-panel-count">{meta.count}</span>
        <button
          type="button"
          className="ops-panel-close"
          onClick={() => setOpsCategory(null)}
          aria-label="Close panel"
        >
          ×
        </button>
      </header>

      <div className="ops-panel-body">
        {meta.isEmpty ? (
          <p className="ops-panel-empty">
            {category === "production"
              ? "No factory or barracks jobs in flight. Open a production-capable building to start one."
              : category === "research"
                ? `No research in flight. Open the Research Center to start a node. Slots ${meta.slots?.used ?? 0} / ${meta.slots?.max ?? 0}.`
                : "No upgrades in progress. Open a building's info popup and tap Upgrade to start one."}
          </p>
        ) : category === "production" && meta.grouped ? (
          <ul className="ops-list">
            {meta.grouped.map((g) => {
              const b = buildings.find((x) => x.id === g.baseBuildingId);
              const family = (b?.family ?? g.family) as BuildingFamily;
              const info = BUILDING_INFO[family];
              const ready = g.jobs.filter((j) => j.state === "complete").length;
              return (
                <li key={g.baseBuildingId} className="ops-row">
                  <FamilyChip family={family} />
                  <div className="ops-row-mid">
                    <span className="ops-row-name">{info.displayName}</span>
                    <span className="ops-row-sub">
                      {g.jobs.length} job{g.jobs.length === 1 ? "" : "s"}
                      {ready > 0 ? <span className="ops-row-sub-ready"> · {ready} ready</span> : null}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ops-row-cta"
                    onClick={() => onShowProduction(g.baseBuildingId)}
                  >
                    Show
                  </button>
                </li>
              );
            })}
          </ul>
        ) : category === "construction" && meta.active ? (
          <ul className="ops-list">
            {meta.active.map((job) => {
              const b = buildings.find((x) => x.id === job.baseBuildingId);
              const family = (b?.family ?? job.family) as BuildingFamily;
              const info = BUILDING_INFO[family];
              const fromLevel = (b?.level ?? job.targetLevel - 1);
              const eta = formatEta(job.startedAtMs + job.durationMs - Date.now());
              return (
                <li key={job.id} className="ops-row">
                  <FamilyChip family={family} />
                  <div className="ops-row-mid">
                    <span className="ops-row-name">{info.displayName}</span>
                    <span className="ops-row-sub">
                      Lv {fromLevel} → Lv {job.targetLevel} · {eta}
                    </span>
                  </div>
                  <div className="ops-row-actions">
                    <button
                      type="button"
                      className="ops-row-cta-secondary"
                      onClick={() => onShowConstruction(job.baseBuildingId)}
                      title="Open building info popup"
                    >
                      Show
                    </button>
                    <button
                      type="button"
                      className="ops-row-cta"
                      onClick={() => onSpeedupConstruction(job.id)}
                      title="Spend diamonds to finish now"
                    >
                      Speed-Up
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : category === "research" && meta.research ? (
          <ul className="ops-list">
            {meta.research.map((job) => {
              const node = RESEARCH_NODE_BY_ID[job.nodeId];
              const eta = formatEta(job.startedAtMs + job.durationMs - Date.now());
              return (
                <li key={job.id} className="ops-row">
                  <ResearchChip />
                  <div className="ops-row-mid">
                    <span className="ops-row-name">{node?.displayName ?? job.nodeId}</span>
                    <span className="ops-row-sub">
                      Tier {node?.tier ?? "?"} · {eta}
                    </span>
                  </div>
                  <div className="ops-row-actions">
                    <button
                      type="button"
                      className="ops-row-cta-secondary"
                      onClick={onOpenResearch}
                      title="Open the research tree"
                    >
                      Show
                    </button>
                    <button
                      type="button"
                      className="ops-row-cta"
                      onClick={() => onSpeedupResearch(job.id)}
                      title="Spend diamonds to finish now"
                    >
                      Speed-Up
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </aside>
  );
}

function ResearchChip(): JSX.Element {
  // RESEARCH_CENTER hue (231°) — same accent the family uses elsewhere.
  const accent = `oklch(58% 0.16 231)`;
  const accentSoft = `oklch(28% 0.04 231)`;
  return (
    <span
      className="ops-row-chip"
      style={{
        ["--ops-chip-accent" as string]: accent,
        ["--ops-chip-accent-soft" as string]: accentSoft,
      }}
      aria-hidden="true"
    >
      RC
    </span>
  );
}

interface ProductionGroup {
  readonly baseBuildingId: string;
  readonly family: BuildingFamily;
  readonly jobs: readonly ProductionJobDto[];
}

function groupProduction(jobs: readonly ProductionJobDto[]): readonly ProductionGroup[] {
  const live = jobs.filter((j) => j.state === "active" || j.state === "queued" || j.state === "complete");
  const map = new Map<string, ProductionGroup>();
  for (const job of live) {
    const prev = map.get(job.baseBuildingId);
    if (prev) {
      map.set(job.baseBuildingId, { ...prev, jobs: [...prev.jobs, job] });
    } else {
      map.set(job.baseBuildingId, {
        baseBuildingId: job.baseBuildingId,
        family: job.family as BuildingFamily,
        jobs: [job],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.family.localeCompare(b.family));
}

function FamilyChip({ family }: { readonly family: BuildingFamily }): JSX.Element {
  const info = BUILDING_INFO[family];
  const accent = `oklch(58% 0.16 ${info.accentHue})`;
  const accentSoft = `oklch(28% 0.04 ${info.accentHue})`;
  const initials = familyInitials(info.displayName);
  return (
    <span
      className="ops-row-chip"
      style={{
        ["--ops-chip-accent" as string]: accent,
        ["--ops-chip-accent-soft" as string]: accentSoft,
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

function familyInitials(displayName: string): string {
  const parts = displayName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const word = parts[0] ?? "";
    return word.slice(0, 2).toUpperCase();
  }
  const a = (parts[0]?.[0] ?? "").toUpperCase();
  const b = (parts[1]?.[0] ?? "").toUpperCase();
  return `${a}${b}`;
}

function formatEta(remainingMs: number): string {
  if (remainingMs <= 0) return "0s";
  const sec = Math.ceil(remainingMs / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rsec = sec % 60;
  if (min < 60) return `${min}m ${rsec.toString().padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  const rmin = min % 60;
  return `${h}h ${rmin.toString().padStart(2, "0")}m`;
}
